import { HttpClient, HttpContext } from '@angular/common/http';
import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BYPASS_SIN_TOKEN } from '@appNeo/neoCore/interceptors/jwt/jwt.interceptor';
import { DISABLE_DELAY_LOADER, NOT_LOADER } from '@appNeo/neoCore/interceptors/loaderInterceptor/loader.interceptor';
import { TipoPaginadorEnum } from '@appNeo/neoShared/helpers/enums/TipoPaginador.enum';
import { IRespApi } from '@appNeo/neoShared/helpers/interfaces/IResp-api';
import { Paginator } from '@appNeo/neoShared/models/paginator/paginator';
import { AuxiliarService } from '@appNeo/neoShared/services/auxiliar/auxiliar.service';
import { environment } from '@environments/environment';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, Subject, tap, throwError } from 'rxjs';

export interface IPreferencia {
  clave: string;
  valor: string;
}

@Injectable({
  providedIn: 'root'
})
export class PreferenciasService {
  contextosDesactivarDelay = { context: new HttpContext().set(DISABLE_DELAY_LOADER, true) };
  contextoSinToken = { context: new HttpContext().set(BYPASS_SIN_TOKEN, true) };
  contextoSinLoader = { context: new HttpContext().set(NOT_LOADER, true) };
  preferencias$: BehaviorSubject<IPreferencia[]> = new BehaviorSubject<IPreferencia[]>([]);
  urlEntidad = environment.api_base_url_backoffice + '/preferencias';
  public error422Api$: Subject<IRespApi> = new Subject<IRespApi>();

  constructor(
    private auxiliar: AuxiliarService,
    private http: HttpClient,
    ) {}

  set preferencias(preferencias: IPreferencia[]) {
    if (preferencias.length == 1) {
      let pre = this.preferencias$.value;
      let preArray = pre.concat(preferencias);
      this.preferencias$.next(preArray);
    } else {
      this.preferencias$.next(preferencias);
    }
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    let peticion = {
      listadoEntidad: this.getAll().pipe(
        catchError(err => of('sin preferencias'))
      ),
      // claves no devueltas por get all al ser privadas
      clave_matrix_mapaApikeyServidor: this.getApi('matrix_mapaApikeyServidor').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_mapaLatitudDefecto: this.getApi('matrix_mapaLatitudDefecto').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_mapaLongitudDefecto: this.getApi('matrix_mapaLongitudDefecto').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_cookiebot: this.getApi('matrix_cookiebot').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_diasPrevioEventoBloqueo: this.getApi('matrix_diasPrevioEventoBloqueo').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_acceso_alta_reservas_publico: this.getApi('matrix_acceso_alta_reservas_publico').pipe(
        catchError(err => of(null))
      ),
      clave_matrix_numeroMesesVisibilidadCalendarioPublico: this.getApi('matrix_numeroMesesVisibilidadCalendarioPublico').pipe(
        catchError(err => of(null))
      ),
    };
    return forkJoin(peticion);
  }

  update(clave: string, valor: any) {
    console.log('Preferencias update', `${this.urlEntidad}/${clave}`, { valor });
    return this.http.patch(`${this.urlEntidad}/${clave}`, { valor }).pipe(
      map((data: IRespApi) => data['data']),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestPreferencia' + clave);
      })
    );
  }


  getAll(tipoPaginador?: TipoPaginadorEnum, paginator?: Paginator, filtros?: any): Observable<any[]> {
    let request = (tipoPaginador === TipoPaginadorEnum.offline)
    ? this.urlEntidad
    : `${this.urlEntidad}?${this.auxiliar.extraerFiltrosPaginacion(paginator)}${this.auxiliar.extraerFiltros(filtros)}`;
    return this.http.get(request, { ...this.contextosDesactivarDelay }).pipe(
      tap((data: IRespApi) => this.preferencias = data.data),
      map((data: IRespApi) => data.data),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestPreferencias');
      })
    )
  }

  get( clave: string ): string | number {
    let valor = this.preferencias$.value[this.auxiliar.buquedaPosicionPropiedad(this.preferencias$.value, 'clave', clave)]?.valor;
    if (valor) {
      if (Number(valor)) {
        return Number(valor)
      } else {
        return valor;
      } 
    } else {
       return ''
    }
  }

  getApi(id, sinLoader: boolean = false): Observable<IPreferencia> {
    let context = (sinLoader) ? this.contextoSinLoader : this.contextoSinToken;
    return this.http.get(`${this.urlEntidad}/${id}`, { ...context }).pipe(
      tap((data: IRespApi) => {
        let preferencias = this.preferencias$.value;
        let preferencia: IPreferencia = data.data;
        this.preferencias = [preferencia];
      }),
      map((data: IRespApi) => data.data),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestPreferencia');
      })
    );
  }

  private handleError(err: IRespApi, idRequest = '') {
    // dialog específico
    if (err.codigoError === 422) {
      err.request = idRequest;
      this.error422Api$.next(err);
    }

    // pantalla común
    let mensaje = err?.mensaje;
    if (err.codigoError === 403) {
      mensaje = `${err.mensaje}: `;
      err.data.forEach(element => {
        mensaje += element.mensaje;
      });
    }
    if (err.codigoError === 404) {
      let avisos = '';
      if (err.data) {
        if (typeof err.data === 'string') {
          avisos = err.data;
        } else {
          err.data.forEach(element => {
            if (element.id === 999) {
              avisos = ' ' + element.mensaje + ' ';
            }
          });
        }
      }
      mensaje = err?.mensaje + avisos;
    }
    return throwError({ 'error': true, 'data': err.data, mensaje });
  }
}

